import React, { useState, useCallback, useRef } from 'react';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import CollapsibleDaySection from './CollapsibleDaySection';
import { getDateForDay } from './plannerHelpers';
import { ChevronsUpDown, Map as MapIcon, MapPin, Sparkles, Plus } from 'lucide-react';

export default function OverviewView({
  days,
  activities,
  itinerary,
  sensors,
  activeId,
  dragOverDay,
  dayLocationLabels,
  dayTitles,
  generatingActivities,
  highlightedDays,
  getAccommodationForDay,
  onDragStart,
  onDragEnd,
  onDrop,
  onDragOver,
  onDragLeave,
  onAddActivity,
  onEditActivity,
  onDeleteActivity,
  onSaveNotes,
  onAddAccommodationRange,
  onDeleteAccommodation,
  onSetHotelDropPrompt,
  onDeleteDay,
  onDayTitleSave,
  onSwapDays,
  onOpenMap,
  selectedActivityId,
  onActivitySelect,
  onOpenAI,
}) {
  const [expandedDays, setExpandedDays] = useState(() => new Set(days));
  const dayRefs = useRef({});

  const toggleDay = useCallback((day) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedDays(new Set(days));
  }, [days]);

  const collapseAll = useCallback(() => {
    setExpandedDays(new Set());
  }, []);

  const allExpanded = days.every(d => expandedDays.has(d));

  // Map day-click handler: expand day + scroll to it
  const handleMapDayClick = useCallback((day) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      next.add(day);
      return next;
    });
    // Scroll to the day section
    setTimeout(() => {
      dayRefs.current[day]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // Auto-expand day on drag hover (500ms timer)
  const dragTimerRef = useRef(null);
  const handleDragOverDay = useCallback((e, day) => {
    onDragOver(e, day);
    if (!expandedDays.has(day)) {
      if (dragTimerRef.current?.day !== day) {
        clearTimeout(dragTimerRef.current?.timer);
        dragTimerRef.current = {
          day,
          timer: setTimeout(() => {
            setExpandedDays(prev => new Set([...prev, day]));
          }, 500),
        };
      }
    }
  }, [onDragOver, expandedDays]);

  const handleDragLeave = useCallback((e) => {
    onDragLeave(e);
    clearTimeout(dragTimerRef.current?.timer);
    dragTimerRef.current = null;
  }, [onDragLeave]);

  // Blank state — no activities yet and not generating
  if (activities.length === 0 && !generatingActivities) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[360px] text-center px-6 py-8">
        <div className="w-16 h-16 rounded-2xl bg-coral-50 flex items-center justify-center mb-5">
          <MapPin size={28} className="text-coral-400" />
        </div>
        <h3 className="text-xl font-black text-charcoal-500 mb-2">
          {itinerary?.destination ? `Your ${itinerary.destination} trip is ready` : 'Your trip is ready to fill'}
        </h3>
        <p className="text-sm text-charcoal-400 mb-8 max-w-xs leading-relaxed">
          Generate a full day-by-day itinerary with AI, or start adding activities yourself.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onOpenAI && (
            <button
              onClick={onOpenAI}
              className="flex items-center gap-2 px-5 py-3 bg-coral-400 hover:bg-coral-500 text-white font-bold rounded-xl transition-colors text-sm"
            >
              <Sparkles size={16} />
              Generate with AI
            </button>
          )}
          <button
            onClick={() => onAddActivity(days[0])}
            className="flex items-center gap-2 px-5 py-3 bg-white border border-platinum-200 hover:border-coral-300 hover:text-coral-500 text-charcoal-500 font-semibold rounded-xl transition-colors text-sm"
          >
            <Plus size={16} />
            Add manually
          </button>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={(event) => {
        clearTimeout(dragTimerRef.current?.timer);
        dragTimerRef.current = null;
        onDragEnd(event);
      }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            onClick={allExpanded ? collapseAll : expandAll}
            className="flex items-center gap-1 text-xs text-charcoal-400 hover:text-charcoal-500 px-2 py-1 rounded-lg hover:bg-platinum-50 transition-colors"
          >
            <ChevronsUpDown size={13} />
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
        {onOpenMap && activities.some(a => a.latitude && a.longitude) && (
          <button
            onClick={onOpenMap}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-charcoal-400 hover:text-coral-500 hover:bg-coral-50 transition-colors"
          >
            <MapIcon size={13} />
            View map
          </button>
        )}
      </div>

      {/* Day sections */}
      <div className="space-y-4">
        {(() => {
          // Compute city runs for multi-city headers
          const cityRunStarts = {};
          let prevCity = null;
          let runStartDay = null;
          days.forEach(day => {
            const city = dayLocationLabels[day];
            if (city !== prevCity) {
              runStartDay = day;
              cityRunStarts[day] = { city, count: 0 };
              prevCity = city;
            }
            if (runStartDay !== null && cityRunStarts[runStartDay]) {
              cityRunStarts[runStartDay].count++;
            }
          });
          const distinctCities = [...new Set(days.map(d => dayLocationLabels[d]).filter(Boolean))];
          const isMultiCity = distinctCities.length > 1;

          return days.map(day => {
            const dayActivities = activities.filter(a => a.day_number === day);
            const isDragOver = dragOverDay === day;
            const locationLabel = dayLocationLabels[day];
            const dayLabel = itinerary.start_date
              ? `${getDateForDay(itinerary.start_date, day)}`
              : `Day ${day}`;
            const skeletonCount = itinerary.travel_pace === 'relaxed' ? 2 : itinerary.travel_pace === 'packed' ? 4 : 3;
            const dayAccommodation = getAccommodationForDay(day);
            const cityRun = isMultiCity ? cityRunStarts[day] : null;

            return (
              <React.Fragment key={day}>
                {cityRun && cityRun.city && (
                  <div className="flex items-center gap-2 px-1 pt-2 pb-0.5">
                    <MapPin size={11} className="text-coral-400 flex-shrink-0" />
                    <span className="text-xs font-bold text-charcoal-500 uppercase tracking-wider">{cityRun.city}</span>
                    <span className="text-xs text-platinum-400">{cityRun.count} {cityRun.count === 1 ? 'day' : 'days'}</span>
                    <div className="flex-1 h-px bg-platinum-100" />
                  </div>
                )}
                <div ref={(el) => { dayRefs.current[day] = el; }}>
                  <CollapsibleDaySection
                    day={day}
                    dayActivities={dayActivities}
                    isDragOver={isDragOver}
                    dayLabel={dayLabel}
                    dayTitle={dayTitles?.[day] || ''}
                    autoTitle={locationLabel || ''}
                    dayAccommodation={dayAccommodation}
                    skeletonCount={skeletonCount}
                    generatingActivities={generatingActivities}
                    isHighlighted={highlightedDays && highlightedDays.has(day)}
                    itinerary={itinerary}
                    onDrop={(e) => onDrop(e, day)}
                    onDragOver={(e) => handleDragOverDay(e, day)}
                    onDragLeave={handleDragLeave}
                    onAddActivity={onAddActivity}
                    onEditActivity={onEditActivity}
                    onDeleteActivity={onDeleteActivity}
                    onSaveNotes={onSaveNotes}
                    onDeleteAccommodation={onDeleteAccommodation}
                    onAddAccommodation={onAddAccommodationRange}
                    onHotelDrop={onSetHotelDropPrompt}
                    onDeleteDay={onDeleteDay}
                    onDayTitleSave={onDayTitleSave}
                    onSwapDays={onSwapDays}
                    totalDays={days.length}
                    expanded={expandedDays.has(day)}
                    onToggle={toggleDay}
                    selectedActivityId={selectedActivityId}
                    onActivitySelect={onActivitySelect}
                  />
                </div>
              </React.Fragment>
            );
          });
        })()}
      </div>

      <DragOverlay>
        {activeId ? (
          <div className="bg-white p-3 rounded-lg shadow-lg border-2 border-coral-400 max-w-[240px]">
            <p className="text-sm font-medium text-charcoal-500 truncate">
              {activities.find(a => a.id === activeId)?.title}
            </p>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
